import { fieldStateCss } from '@src/components/common/fieldStateStyles';
import { InfoIconSmall } from '@src/svgs/InfoIconSmall';
import { StarFilledIcon } from '@src/svgs/StarFilledIcon';
import {
  CancelIcon,
  CompareIcon,
  ConfirmIcon,
  DropdownChevronIcon,
  HistoryClockIcon,
  KebabMenuIcon,
  RenamePencilIcon,
  TrashIcon,
  VersionCheckIcon,
} from '@src/svgs/VersionTrackerIcons';
import { format, isSameDay, startOfDay, subDays } from 'date-fns';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import { useOutsideAlerter } from '../../transcript/useClickOutside';

import type {
  ContentVersion,
  GenerationDetails,
} from '../../../types.ts/versioning';

export type { ContentVersion, GenerationDetails };

/** Group entries into Today / Yesterday / "MMM d, yyyy". Mirrors Google Docs' version history panel. */
function groupByDay(
  versions: ContentVersion[],
): Array<{ key: string; label: string; items: ContentVersion[] }> {
  const now = new Date();
  const today = startOfDay(now);
  const yesterday = subDays(today, 1);

  const buckets = new Map<string, { label: string; items: ContentVersion[] }>();
  for (const v of versions) {
    const day = startOfDay(new Date(v.timestamp));
    const key = day.toISOString();
    let label: string;
    if (isSameDay(day, today)) label = 'Today';
    else if (isSameDay(day, yesterday)) label = 'Yesterday';
    else label = format(day, 'MMM d, yyyy');

    if (!buckets.has(key)) buckets.set(key, { label, items: [] });
    buckets.get(key)!.items.push(v);
  }
  return Array.from(buckets.entries()).map(([key, { label, items }]) => ({
    key,
    label,
    items,
  }));
}

type Props = {
  versions: ContentVersion[];
  currentVersionId: string | null;
  onSelectVersion: (version: ContentVersion) => void;
  onCompareToLatest?: () => void;
  isComparing?: boolean;
  onRenameVersion?: (versionId: string, newLabel: string) => void;
  onDeleteVersion?: (versionId: string) => void;
  /**
   * Inline "Save as version" action rendered at the top of the dropdown.
   * When omitted, the row is hidden — preserves backwards-compat for callers
   * that only want to show history.
   */
  onSaveAsVersion?: (name: string) => void | Promise<void>;
};

const ContentVersionTracker = ({
  versions,
  currentVersionId,
  onSelectVersion,
  onCompareToLatest,
  isComparing,
  onRenameVersion,
  onDeleteVersion,
  onSaveAsVersion,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [infoTooltipVersionId, setInfoTooltipVersionId] = useState<
    string | null
  >(null);
  const [infoTooltipPos, setInfoTooltipPos] = useState({ top: 0, left: 0 });
  const [renameValue, setRenameValue] = useState('');
  const [showNamedOnly, setShowNamedOnly] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [creationName, setCreationName] = useState('');
  const [isSavingVersion, setIsSavingVersion] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const createInputRef = useRef<HTMLInputElement>(null);

  // Filter out soft-deleted versions for display
  const activeVersions = useMemo(
    () => versions.filter(v => !v.deleted),
    [versions],
  );
  const displayedVersions = useMemo(
    () =>
      showNamedOnly ? activeVersions.filter(v => v.isNamed) : activeVersions,
    [activeVersions, showNamedOnly],
  );
  const groupedVersions = useMemo(
    () => groupByDay(displayedVersions),
    [displayedVersions],
  );

  const currentVersion = activeVersions.find(v => v.id === currentVersionId);
  const currentIndex = activeVersions.findIndex(v => v.id === currentVersionId);

  const closeDropdown = useCallback(() => setIsOpen(false), []);
  useOutsideAlerter(dropdownRef, closeDropdown);

  const handleSelect = (version: ContentVersion) => {
    onSelectVersion(version);
    setIsOpen(false);
  };

  const handleMenuToggle = (e: React.MouseEvent, versionId: string) => {
    e.stopPropagation();
    if (openMenuId === versionId) {
      setOpenMenuId(null);
      setMenuPosition(null);
    } else {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
      setOpenMenuId(versionId);
    }
  };

  const handleRenameClick = (e: React.MouseEvent, version: ContentVersion) => {
    e.stopPropagation();
    setRenamingId(version.id);
    setRenameValue(version.label);
    setOpenMenuId(null);
    setMenuPosition(null);
  };

  const handleRenameSubmit = (versionId: string) => {
    if (renameValue.trim() && onRenameVersion) {
      onRenameVersion(versionId, renameValue.trim());
    }
    setRenamingId(null);
    setRenameValue('');
  };

  const handleRenameCancel = () => {
    setRenamingId(null);
    setRenameValue('');
  };

  const beginCreation = useCallback(() => {
    setIsCreating(true);
    setCreationName('');
    // Focus on the next frame so the input is mounted.
    requestAnimationFrame(() => createInputRef.current?.focus());
  }, []);

  const cancelCreation = useCallback(() => {
    setIsCreating(false);
    setCreationName('');
  }, []);

  const submitCreation = useCallback(async () => {
    const trimmed = creationName.trim();
    if (!trimmed || !onSaveAsVersion || isSavingVersion) return;
    setIsSavingVersion(true);
    try {
      await onSaveAsVersion(trimmed);
      // The new version flows back in through `versions`; the hook selects
      // it automatically (latest), so no extra selection plumbing needed.
      setIsCreating(false);
      setCreationName('');
    } finally {
      setIsSavingVersion(false);
    }
  }, [creationName, onSaveAsVersion, isSavingVersion]);

  const handleCreationKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void submitCreation();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelCreation();
    }
  };

  // Reset the inline creation state whenever the dropdown closes so the
  // next open lands on the idle row.
  useEffect(() => {
    if (!isOpen) {
      setIsCreating(false);
      setCreationName('');
    }
  }, [isOpen]);

  const handleDeleteClick = (e: React.MouseEvent, versionId: string) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setMenuPosition(null);
    onDeleteVersion?.(versionId);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent, versionId: string) => {
    if (e.key === 'Enter') {
      handleRenameSubmit(versionId);
    } else if (e.key === 'Escape') {
      handleRenameCancel();
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        openMenuId &&
        !target.closest('[data-menu-trigger]') &&
        !target.closest('[data-menu-content]')
      ) {
        setOpenMenuId(null);
        setMenuPosition(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const hasVersions = activeVersions.length > 0;
  const isViewingPastVersion = currentIndex > 0;

  return (
    <Container ref={dropdownRef} data-testid="article-version-tracker">
      <HistoryIcon>
        <HistoryClockIcon />
      </HistoryIcon>

      <DropdownButton
        onClick={() => (hasVersions || onSaveAsVersion) && setIsOpen(!isOpen)}
        isOpen={isOpen}
        disabled={!hasVersions && !onSaveAsVersion}
      >
        <ButtonContent>
          <VersionInfo>
            <VersionLabel>
              {currentVersion?.label ||
                (hasVersions ? 'Select version' : 'Current Version')}
            </VersionLabel>
            <VersionTime>
              {currentVersion
                ? format(currentVersion.timestamp, 'MMM d, h:mm a')
                : 'No history yet'}
              {currentIndex === 0 && hasVersions && (
                <LatestBadge>Latest</LatestBadge>
              )}
            </VersionTime>
          </VersionInfo>
          {hasVersions && (
            <ChevronIcon isOpen={isOpen}>
              <DropdownChevronIcon />
            </ChevronIcon>
          )}
        </ButtonContent>
      </DropdownButton>

      {isViewingPastVersion && onCompareToLatest && (
        <CompareButton
          onClick={onCompareToLatest}
          isActive={isComparing}
          data-testid="article-version-compare"
        >
          <CompareIcon />
          {isComparing ? 'Hide Diff' : 'Compare'}
        </CompareButton>
      )}

      {isOpen && (hasVersions || onSaveAsVersion) && (
        <DropdownMenu>
          {onSaveAsVersion && (
            <CreateSection data-testid="article-version-create-section">
              {isCreating ? (
                <CreateInputRow>
                  <CreateStarIcon aria-hidden="true">
                    <StarFilledIcon />
                  </CreateStarIcon>
                  <CreateInput
                    ref={createInputRef}
                    value={creationName}
                    onChange={e => setCreationName(e.target.value)}
                    onKeyDown={handleCreationKeyDown}
                    placeholder="e.g., Before audience rewrite"
                    maxLength={80}
                    disabled={isSavingVersion}
                    data-testid="article-version-create-input"
                  />
                  <CreateActions>
                    <CreateCancelButton
                      type="button"
                      onClick={cancelCreation}
                      disabled={isSavingVersion}
                    >
                      Cancel
                    </CreateCancelButton>
                    <CreateSaveButton
                      type="button"
                      onClick={() => void submitCreation()}
                      disabled={!creationName.trim() || isSavingVersion}
                      data-testid="article-version-create-submit"
                    >
                      {isSavingVersion ? 'Saving…' : 'Save'}
                    </CreateSaveButton>
                  </CreateActions>
                </CreateInputRow>
              ) : (
                <CreateButton
                  type="button"
                  onClick={beginCreation}
                  data-testid="article-version-create-button"
                >
                  <CreateStarIcon aria-hidden="true">
                    <StarFilledIcon />
                  </CreateStarIcon>
                  <span>Save as version</span>
                </CreateButton>
              )}
            </CreateSection>
          )}
          {onSaveAsVersion && hasVersions && <MenuDivider />}
          {hasVersions && (
            <MenuHeader>
              <MenuTitle>Version History</MenuTitle>
              <HeaderRight>
                <FilterToggle
                  type="button"
                  $active={showNamedOnly}
                  onClick={() => setShowNamedOnly(v => !v)}
                  data-testid="article-version-named-only-toggle"
                  aria-pressed={showNamedOnly}
                >
                  Named only
                </FilterToggle>
                <VersionCount>
                  {displayedVersions.length} version
                  {displayedVersions.length !== 1 ? 's' : ''}
                </VersionCount>
              </HeaderRight>
            </MenuHeader>
          )}
          {hasVersions && <MenuDivider />}
          {hasVersions && (
            <MenuList>
              {displayedVersions.length === 0 && showNamedOnly && (
                <EmptyState>
                  No named versions yet. Use "Save as version" to pin a
                  checkpoint.
                </EmptyState>
              )}
              {groupedVersions.map(group => (
                <React.Fragment key={group.key}>
                  <GroupHeader>{group.label}</GroupHeader>
                  {group.items.map(version => {
                    const activeIndex = activeVersions.findIndex(
                      v => v.id === version.id,
                    );
                    const isLatest = activeIndex === 0;
                    return (
                      <MenuItem
                        key={version.id}
                        isSelected={version.id === currentVersionId}
                        onClick={() => handleSelect(version)}
                        data-testid={`article-version-${version.id}`}
                      >
                        <MenuItemIcon
                          isSelected={version.id === currentVersionId}
                        >
                          {version.id === currentVersionId ? (
                            <VersionCheckIcon />
                          ) : version.isNamed ? (
                            <StarIndicator aria-label="Named version">
                              <StarFilledIcon width="12" height="12" />
                            </StarIndicator>
                          ) : (
                            <VersionDot />
                          )}
                        </MenuItemIcon>
                        <MenuItemContent>
                          {renamingId === version.id ? (
                            <RenameInputContainer
                              onClick={e => e.stopPropagation()}
                            >
                              <RenameInput
                                value={renameValue}
                                onChange={e => setRenameValue(e.target.value)}
                                onKeyDown={e =>
                                  handleRenameKeyDown(e, version.id)
                                }
                                autoFocus
                                placeholder="Enter version name"
                              />
                              <RenameActions>
                                <RenameButton
                                  onClick={() => handleRenameSubmit(version.id)}
                                >
                                  <ConfirmIcon />
                                </RenameButton>
                                <RenameButton onClick={handleRenameCancel}>
                                  <CancelIcon />
                                </RenameButton>
                              </RenameActions>
                            </RenameInputContainer>
                          ) : (
                            <>
                              <MenuItemLabel>
                                {version.label}
                                {isLatest && <LatestBadge>Latest</LatestBadge>}
                                {version.generationDetails && (
                                  <InfoIconWrapper
                                    onClick={e => e.stopPropagation()}
                                    onMouseEnter={e => {
                                      const rect =
                                        e.currentTarget.getBoundingClientRect();
                                      setInfoTooltipPos({
                                        top: rect.top + rect.height / 2,
                                        left: rect.right + 8,
                                      });
                                      setInfoTooltipVersionId(version.id);
                                    }}
                                    onMouseLeave={() =>
                                      setInfoTooltipVersionId(null)
                                    }
                                  >
                                    <InfoIcon>
                                      <InfoIconSmall />
                                    </InfoIcon>
                                    <InfoTooltip
                                      style={
                                        infoTooltipVersionId === version.id
                                          ? {
                                              display: 'flex',
                                              flexDirection: 'column' as const,
                                              gap: 6,
                                              top: infoTooltipPos.top,
                                              left: infoTooltipPos.left,
                                              transform: 'translateY(-50%)',
                                            }
                                          : undefined
                                      }
                                    >
                                      {version.generationDetails
                                        .templateName && (
                                        <InfoRow>
                                          <InfoLabel>Template</InfoLabel>
                                          <InfoValue>
                                            {
                                              version.generationDetails
                                                .templateName
                                            }
                                          </InfoValue>
                                        </InfoRow>
                                      )}
                                      {version.generationDetails.language && (
                                        <InfoRow>
                                          <InfoLabel>Language</InfoLabel>
                                          <InfoValue>
                                            {version.generationDetails.language}
                                          </InfoValue>
                                        </InfoRow>
                                      )}
                                      {version.generationDetails.speakers &&
                                        version.generationDetails.speakers
                                          .length > 0 && (
                                          <InfoRow>
                                            <InfoLabel>Speakers</InfoLabel>
                                            <InfoValue>
                                              {version.generationDetails.speakers.join(
                                                ', ',
                                              )}
                                            </InfoValue>
                                          </InfoRow>
                                        )}
                                      {version.generationDetails.goal && (
                                        <InfoRow>
                                          <InfoLabel>Goal</InfoLabel>
                                          <InfoValue>
                                            {version.generationDetails.goal}
                                          </InfoValue>
                                        </InfoRow>
                                      )}
                                      {version.generationDetails.audiences &&
                                        version.generationDetails.audiences
                                          .length > 0 && (
                                          <InfoRow>
                                            <InfoLabel>Audience</InfoLabel>
                                            <InfoValue>
                                              {version.generationDetails.audiences.join(
                                                ', ',
                                              )}
                                            </InfoValue>
                                          </InfoRow>
                                        )}
                                      {version.prompt && (
                                        <InfoRow>
                                          <InfoLabel>Prompt</InfoLabel>
                                          <InfoValue>
                                            {version.prompt}
                                          </InfoValue>
                                        </InfoRow>
                                      )}
                                    </InfoTooltip>
                                  </InfoIconWrapper>
                                )}
                              </MenuItemLabel>
                              <MenuItemTime>
                                {format(version.timestamp, 'MMM d · h:mm a')}
                              </MenuItemTime>
                            </>
                          )}
                        </MenuItemContent>
                        {renamingId !== version.id && onRenameVersion && (
                          <MenuButton
                            data-menu-trigger
                            onClick={e => handleMenuToggle(e, version.id)}
                          >
                            <KebabMenuIcon />
                          </MenuButton>
                        )}
                      </MenuItem>
                    );
                  })}
                </React.Fragment>
              ))}
            </MenuList>
          )}
        </DropdownMenu>
      )}
      {openMenuId &&
        menuPosition &&
        (() => {
          const version = groupedVersions
            .flatMap(g => g.items)
            .find(v => v.id === openMenuId);
          if (!version || renamingId === version.id) return null;
          return ReactDOM.createPortal(
            <ContextMenu
              data-menu-content
              $top={menuPosition.top}
              $right={menuPosition.right}
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
            >
              <ContextMenuItem onClick={e => handleRenameClick(e, version)}>
                <RenamePencilIcon />
                Rename
              </ContextMenuItem>
              {onDeleteVersion && activeVersions.length > 1 && (
                <ContextMenuItemDanger
                  onClick={e => handleDeleteClick(e, version.id)}
                >
                  <TrashIcon />
                  Delete
                </ContextMenuItemDanger>
              )}
            </ContextMenu>,
            document.body,
          );
        })()}
    </Container>
  );
};

export default ContentVersionTracker;

const Container = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HistoryIcon = styled.div`
  color: var(--text-muted);
  display: flex;
  align-items: center;
`;

const DropdownButton = styled.button<{ isOpen: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  background: ${props =>
    props.isOpen ? 'var(--sidebar-hover)' : 'transparent'};
  border: 1px solid
    ${props => (props.isOpen ? 'var(--primary)' : 'transparent')};
  border-radius: 8px;
  padding: 6px 10px;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  transition: all 0.2s ease;
  opacity: ${props => (props.disabled ? 0.7 : 1)};

  &:hover {
    background: ${props =>
      props.disabled ? 'transparent' : 'var(--sidebar-hover)'};
    border-color: ${props =>
      props.disabled ? 'transparent' : 'var(--border)'};
  }

  &:focus {
    outline: none;
    border-color: ${props =>
      props.disabled ? 'transparent' : 'var(--primary)'};
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
`;

const VersionInfo = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  white-space: nowrap;
`;

const VersionLabel = styled.span`
  color: var(--foreground);
  font-size: 13px;
  font-weight: 600;
`;

const VersionTime = styled.span`
  color: var(--text-muted);
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LatestBadge = styled.span`
  background: linear-gradient(
    135deg,
    var(--primary) 0%,
    var(--primary-pressed) 100%
  );
  color: var(--background);
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1.4;
`;

const ChevronIcon = styled.div<{ isOpen: boolean }>`
  color: var(--text-muted);
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
  transform: ${props => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const CompareButton = styled.button<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  white-space: nowrap;
  border: 1px solid
    ${props => (props.isActive ? 'var(--accent)' : 'var(--border)')};
  background: ${props =>
    props.isActive ? 'var(--accent-tint)' : 'transparent'};
  color: ${props => (props.isActive ? 'var(--accent)' : 'var(--text-muted)')};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: var(--accent-tint-light);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.4),
    0 2px 8px rgba(0, 0, 0, 0.2);
  min-width: 320px;
  z-index: 1000;
  overflow: visible;
  max-height: 360px;
  display: flex;
  flex-direction: column;
  animation: slideDown 0.15s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CreateSection = styled.div`
  padding: 6px;
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--foreground);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
  text-align: left;

  &:hover {
    background: var(--sidebar-hover);
  }
`;

const CreateStarIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
  flex-shrink: 0;
`;

const CreateInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
`;

const CreateInput = styled.input`
  flex: 1;
  min-width: 0;
  background: var(--surface);
  ${fieldStateCss}
  border-radius: 6px;
  padding: 6px 10px;
  color: var(--foreground);
  font-size: 13px;
  outline: none;

  &::placeholder {
    color: var(--text-muted);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const CreateActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const CreateSaveButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  background: var(--primary);
  color: var(--background);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CreateCancelButton = styled.button`
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: transparent;
  color: var(--foreground);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FilterToggle = styled.button<{ $active: boolean }>`
  background: ${({ $active }) =>
    $active ? 'var(--accent-tint)' : 'transparent'};
  color: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--text-muted)')};
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--accent)' : 'var(--border)')};
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const GroupHeader = styled.div`
  padding: 6px 10px 2px;
  margin-top: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.6px;
  color: var(--text-muted);

  &:first-child {
    margin-top: 0;
  }
`;

const StarIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
`;

const EmptyState = styled.div`
  padding: 16px 14px;
  font-size: 11px;
  color: var(--text-muted);
  line-height: 1.5;
`;

const MenuTitle = styled.span`
  color: var(--accent);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const VersionCount = styled.span`
  color: var(--text-muted);
  font-size: 11px;
`;

const MenuDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--border) 50%,
    transparent 100%
  );
`;

const MenuList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 6px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border);
    border-radius: 3px;
  }
`;

const MenuItem = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${props =>
    props.isSelected ? 'var(--primary-tint-light)' : 'transparent'};
  position: relative;

  &:hover {
    background: ${props =>
      props.isSelected ? 'var(--primary-tint)' : 'var(--sidebar-hover)'};
  }
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.15s ease;
  position: relative;
  margin-left: auto;

  &:hover {
    background: var(--sidebar-active);
    color: var(--foreground);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ContextMenu = styled.div<{ $top?: number; $right?: number }>`
  position: fixed;
  top: ${({ $top }) => $top ?? 0}px;
  right: ${({ $right }) => $right ?? 0}px;
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 4px;
  min-width: 120px;
  z-index: 100001;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  animation: menuFadeIn 0.15s ease-out;

  @keyframes menuFadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ContextMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--foreground);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    background: var(--primary-tint);
    color: var(--primary);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ContextMenuItemDanger = styled(ContextMenuItem)`
  &:hover {
    background: rgba(239, 68, 68, 0.15); /* destructive tint */
    color: var(--destructive);
  }
`;

const RenameInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const RenameInput = styled.input`
  flex: 1;
  background: var(--sidebar-hover);
  ${fieldStateCss}
  border-radius: 6px;
  padding: 6px 10px;
  color: var(--foreground);
  font-size: 13px;
  font-weight: 500;
  outline: none;

  &::placeholder {
    color: var(--text-muted);
  }
`;

const RenameActions = styled.div`
  display: flex;
  gap: 4px;
`;

const RenameButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 4px;
  cursor: pointer;
  color: var(--text-muted);
  transition: all 0.15s ease;

  &:hover {
    background: var(--primary-tint);
    border-color: var(--primary);
    color: var(--primary);
  }

  &:last-child:hover {
    background: rgba(239, 68, 68, 0.15); /* destructive tint */
    border-color: var(--destructive);
    color: var(--destructive);
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const MenuItemIcon = styled.div<{ isSelected: boolean }>`
  color: ${props =>
    props.isSelected ? 'var(--primary)' : 'var(--text-muted)'};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const VersionDot = styled.div`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--text-muted);
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const MenuItemLabel = styled.span`
  color: var(--foreground);
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MenuItemTime = styled.span`
  color: var(--text-muted);
  font-size: 11px;
  white-space: nowrap;
  margin-left: auto;
  flex-shrink: 0;
`;

const InfoIconWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const InfoTooltip = styled.div`
  display: none;
  position: fixed;
  z-index: 100005;
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 12px;
  min-width: 200px;
  max-width: 280px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  pointer-events: none;
`;

const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--text-muted);
  cursor: default;
  transition: color 0.15s ease;

  &:hover {
    color: var(--primary);
  }
`;

const InfoRow = styled.div`
  display: flex;
  gap: 8px;
  font-size: 11px;
  line-height: 1.4;
`;

const InfoLabel = styled.span`
  color: var(--text-muted);
  font-weight: 500;
  min-width: 60px;
  flex-shrink: 0;
`;

const InfoValue = styled.span`
  color: var(--foreground);
  font-weight: 400;
  word-break: break-word;
`;
